import React from 'react';
import Layout from '../../components/Layout';
import {
  Star,
  Check,
  Clock,
  Shield,
  Gift,
  TrendingUp,
  ArrowRight,
  Users,
} from 'lucide-react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

const Bwin: React.FC = () => {
  const ratingData = [
    { category: 'Sécurité', value: 95 },
    { category: 'Bonus', value: 90 },
    { category: 'Sports', value: 92 },
    { category: 'Retrait', value: 88 },
    { category: 'Support', value: 85 },
  ];

  const payoutData = [
    { method: 'Carte Bancaire', time: 24, fee: 0 },
    { method: 'PayPal', time: 24, fee: 0 },
    { method: 'Skrill', time: 12, fee: 2 },
    { method: 'Neteller', time: 12, fee: 2 },
    { method: 'Virement Bancaire', time: 72, fee: 0 },
  ];

  const gameDistribution = [
    { name: 'Football', value: 45, color: '#6366f1' },
    { name: 'Tennis', value: 25, color: '#8b5cf6' },
    { name: 'Basketball', value: 20, color: '#ec4899' },
    { name: 'Autres', value: 10, color: '#f59e0b' },
  ];

  const monthlyTrend = [
    { month: 'Jan', users: 12000, revenue: 450000 },
    { month: 'Fév', users: 13500, revenue: 520000 },
    { month: 'Mar', users: 15000, revenue: 580000 },
    { month: 'Avr', users: 16800, revenue: 640000 },
    { month: 'Mai', users: 18500, revenue: 720000 },
    { month: 'Juin', users: 20000, revenue: 800000 },
  ];

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Review',
    itemReviewed: {
      '@type': 'Organization',
      name: 'bwin',
      description: 'Site de paris sportifs licencié',
    },
    author: {
      '@type': 'Organization',
      name: 'Primorixx',
    },
    reviewRating: {
      '@type': 'Rating',
      ratingValue: '4.8',
      bestRating: '5',
    },
    reviewBody:
      'bwin offre une expérience complète de paris sportifs en ligne avec des bonus généreux, une vaste sélection de sports et des paiements rapides. Recommandé pour les parieurs français.',
  };

  return (
    <Layout
      title="Analyse Complète - bwin"
      description="Analyse détaillée du bookmaker bwin: bonus, sports, sécurité, méthodes de paiement et bien plus encore. Note 4.8/5."
      canonicalUrl="/offres/bwin"
      jsonLd={jsonLd}
      ogImage="/images/offers/bwin.webp"
    >
      <div className="min-h-screen pt-20">
        {/* Hero Section */}
        <section className="py-20 bg-gradient-to-b from-dark-bg to-dark-surface relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <img
              src="/images/offers/bwin.webp"
              alt="bwin"
              className="w-full h-full object-cover"
              loading="eager"
            />
          </div>
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto">
              <div className="flex flex-col md:flex-row items-center md:items-start gap-8">
                <div className="flex-shrink-0">
                  <img
                    src="/images/offers/bwin.webp"
                    alt="bwin"
                    className="w-48 h-48 object-contain bg-dark-card p-4 rounded-xl border border-dark-border"
                    loading="eager"
                  />
                </div>
                <div className="flex-1 text-center md:text-left">
                  <h1 className="text-5xl md:text-6xl font-bold mb-4 gradient-text">
                    bwin - Analyse Complète
                  </h1>
                  <div className="flex items-center justify-center md:justify-start space-x-4 mb-4">
                    <div className="flex items-center space-x-1">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          className={`w-6 h-6 ${
                            i < 5
                              ? 'fill-yellow-400 text-yellow-400'
                              : 'text-dark-border'
                          }`}
                        />
                      ))}
                      <span className="text-2xl font-bold text-white ml-2">
                        4.8
                      </span>
                    </div>
                  </div>
                  <p className="text-xl text-dark-muted mb-6">
                    L'un des bookmakers les plus fiables en France avec des bonus
                    généreux et des paiements rapides
                  </p>
                  <div className="flex flex-wrap gap-4 justify-center md:justify-start">
                    <div className="flex items-center space-x-2 bg-dark-card px-4 py-2 rounded-lg">
                      <Gift className="w-5 h-5 text-dark-accent" />
                      <span className="text-white font-semibold">
                        100% jusqu'à 100€
                      </span>
                    </div>
                    <div className="flex items-center space-x-2 bg-dark-card px-4 py-2 rounded-lg">
                      <Clock className="w-5 h-5 text-dark-accent" />
                      <span className="text-white font-semibold">24-48h</span>
                    </div>
                    <div className="flex items-center space-x-2 bg-dark-card px-4 py-2 rounded-lg">
                      <Shield className="w-5 h-5 text-dark-accent" />
                      <span className="text-white font-semibold">
                        Licencié
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Key Features */}
        <section className="py-20 bg-dark-surface">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-5xl mx-auto">
              {[
                { icon: Gift, label: 'Bonus', value: '100% jusqu\'à 100€' },
                { icon: Clock, label: 'Retrait', value: '24-48h' },
                { icon: Users, label: 'Sports', value: '500+' },
                { icon: Shield, label: 'Licence', value: 'Vérifiée' },
              ].map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <div
                    key={index}
                    className="bg-dark-card border border-dark-border rounded-xl p-6 text-center"
                  >
                    <Icon className="w-8 h-8 text-dark-accent mx-auto mb-3" />
                    <div className="text-2xl font-bold text-white mb-1">
                      {feature.value}
                    </div>
                    <div className="text-sm text-dark-muted">{feature.label}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Rating Breakdown */}
        <section className="py-20 bg-dark-bg">
          <div className="container mx-auto px-4">
            <h2 className="text-4xl font-bold mb-12 gradient-text text-center">
              Analyse Détaillée par Catégorie
            </h2>
            <div className="max-w-4xl mx-auto">
              <div className="bg-dark-card border border-dark-border rounded-xl p-8">
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={ratingData} layout="vertical">
                    <XAxis type="number" domain={[0, 100]} stroke="#a0a0a0" />
                    <YAxis
                      dataKey="category"
                      type="category"
                      stroke="#a0a0a0"
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#1a1a2e',
                        border: '1px solid #2a2a3e',
                        borderRadius: '8px',
                      }}
                    />
                    <Bar dataKey="value" radius={[0, 8, 8, 0]}>
                      {ratingData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={
                            ['#6366f1', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981'][
                              index
                            ]
                          }
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </section>

        {/* Game Distribution */}
        <section className="py-20 bg-dark-surface">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
              <div className="bg-dark-card border border-dark-border rounded-xl p-8">
                <h3 className="text-2xl font-bold text-white mb-6">
                  Répartition des Sports
                </h3>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={gameDistribution}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) =>
                        `${name} ${((percent || 0) * 100).toFixed(0)}%`
                      }
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {gameDistribution.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#1a1a2e',
                        border: '1px solid #2a2a3e',
                        borderRadius: '8px',
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              <div className="bg-dark-card border border-dark-border rounded-xl p-8">
                <h3 className="text-2xl font-bold text-white mb-6">
                  Croissance Mensuelle
                </h3>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={monthlyTrend}>
                    <XAxis dataKey="month" stroke="#a0a0a0" />
                    <YAxis stroke="#a0a0a0" yAxisId="left" />
                    <YAxis
                      stroke="#a0a0a0"
                      yAxisId="right"
                      orientation="right"
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#1a1a2e',
                        border: '1px solid #2a2a3e',
                        borderRadius: '8px',
                      }}
                    />
                    <Legend />
                    <Line
                      yAxisId="left"
                      type="monotone"
                      dataKey="users"
                      stroke="#6366f1"
                      strokeWidth={2}
                      name="Utilisateurs"
                    />
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="revenue"
                      stroke="#8b5cf6"
                      strokeWidth={2}
                      name="Revenus (€)"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </section>

        {/* Payout Methods */}
        <section className="py-20 bg-dark-bg">
          <div className="container mx-auto px-4">
            <h2 className="text-4xl font-bold mb-12 gradient-text text-center">
              Méthodes de Paiement
            </h2>
            <div className="max-w-4xl mx-auto">
              <div className="bg-dark-card border border-dark-border rounded-xl overflow-hidden">
                <table className="w-full">
                  <thead className="bg-dark-surface">
                    <tr>
                      <th className="px-6 py-4 text-left text-white font-semibold">
                        Méthode
                      </th>
                      <th className="px-6 py-4 text-left text-white font-semibold">
                        Délai
                      </th>
                      <th className="px-6 py-4 text-left text-white font-semibold">
                        Frais
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {payoutData.map((method, index) => (
                      <tr
                        key={index}
                        className="border-t border-dark-border hover:bg-dark-surface/50 transition-colors"
                      >
                        <td className="px-6 py-4 text-white font-medium">
                          {method.method}
                        </td>
                        <td className="px-6 py-4 text-dark-muted">
                          {method.time}h
                        </td>
                        <td className="px-6 py-4 text-dark-muted">
                          {method.fee > 0 ? `€${method.fee}` : 'Gratuit'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </section>

        {/* Pros and Cons */}
        <section className="py-20 bg-dark-surface">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              <div className="bg-dark-card border border-dark-border rounded-xl p-8">
                <h3 className="text-2xl font-bold text-white mb-6 flex items-center">
                  <Check className="w-6 h-6 text-green-400 mr-2" />
                  Avantages
                </h3>
                <ul className="space-y-3">
                  {[
                    'Bonus de bienvenue généreux de 100% jusqu\'à 100€',
                    'Délai de paiement rapide (24-48 heures)',
                    'Vaste sélection de plus de 500 sports',
                    'Interface moderne et intuitive',
                    'Licence vérifiée et fiable',
                    'Support client en français',
                  ].map((pro, index) => (
                    <li
                      key={index}
                      className="flex items-start text-dark-muted"
                    >
                      <Check className="w-5 h-5 text-green-400 mr-3 flex-shrink-0 mt-0.5" />
                      {pro}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-dark-card border border-dark-border rounded-xl p-8">
                <h3 className="text-2xl font-bold text-white mb-6 flex items-center">
                  <span className="w-6 h-6 text-red-400 mr-2 text-2xl">✕</span>
                  Points à Considérer
                </h3>
                <ul className="space-y-3">
                  {[
                    'Quelques restrictions peuvent s\'appliquer au bonus',
                    'Dépôt minimum de €10',
                    'Conditions de mise de 35x',
                  ].map((con, index) => (
                    <li
                      key={index}
                      className="flex items-start text-dark-muted"
                    >
                      <span className="w-5 h-5 text-red-400 mr-3 flex-shrink-0 mt-0.5 text-xl">
                        •
                      </span>
                      {con}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-dark-bg">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center bg-dark-card border border-dark-border rounded-xl p-8">
              <h2 className="text-3xl font-bold mb-4 gradient-text">
                Prêt à Commencer?
              </h2>
              <p className="text-dark-muted mb-6">
                Profitez du bonus de bienvenue exclusif de bwin et commencez à
                parier dès aujourd'hui!
              </p>
              <a
                href="#"
                className="inline-flex items-center px-8 py-4 bg-dark-accent hover:bg-dark-accentHover text-white rounded-lg font-semibold transition-colors"
              >
                Réclamer le Bonus
                <ArrowRight className="ml-2 w-5 h-5" />
              </a>
              <p className="text-xs text-dark-muted mt-4">
                18+ | Jouez de manière responsable | Conditions générales s'appliquent
              </p>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default Bwin;

